/**
 * Reviews on the public itinerary view (U6) — the client surface for the
 * `itineraryReview` router.
 *
 * Gating mirrors `canReviewItinerary` on the server so the create affordance
 * is HIDDEN rather than rendered-then-rejected: the owner, an anonymous
 * viewer, and a non-public / archived target never see the form. A duplicate
 * create still can't be ruled out client-side (another tab), so CONFLICT is
 * caught and turned into an edit path rather than a raw error.
 *
 * Aggregates (`averageRating` / `reviewCount`) live on the itinerary document,
 * so every mutation invalidates `itinerary.getBySlug` alongside the review
 * queries.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { Heading2, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { StarDisplay } from '../reviews/ReviewRatingDisplays';
import { formatTrpcError } from '../../utils/formatTrpcError';
import { AddItineraryReview, type ItineraryReviewDraft } from './AddItineraryReview';
import { ItineraryReviewRow } from './ItineraryReviewRow';

const PAGE_SIZE = 20;

interface ItineraryReviewsSectionProps {
  itineraryId: string;
  /** Needed to invalidate the itinerary query that carries the aggregates. */
  slug: string;
  averageRating: number;
  reviewCount: number;
  /** The signed-in viewer owns this itinerary (they may never review it). */
  isOwner: boolean;
  /** Target is public + published — the server's precondition for a review. */
  isReviewableTarget: boolean;
}

/** tRPC error code, when the thrown value carries one. */
const errorCode = (error: unknown): string | undefined =>
  (error as { data?: { code?: string } } | null)?.data?.code;

export const ItineraryReviewsSection = ({
  itineraryId,
  slug,
  averageRating,
  reviewCount,
  isOwner,
  isReviewableTarget,
}: ItineraryReviewsSectionProps) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const utils = trpc.useUtils();

  const [limit, setLimit] = useState(PAGE_SIZE);
  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [conflictNotice, setConflictNotice] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [flagTargetId, setFlagTargetId] = useState<string | null>(null);

  const listQuery = trpc.itineraryReview.listForItinerary.useQuery({
    itineraryId,
    limit,
    offset: 0,
    includeFlagged: false,
  });

  // Own-review lookup is a dedicated query, not a scan of the loaded page:
  // past the first page the scan would miss it and the form would CONFLICT.
  const mineQuery = trpc.itineraryReview.getMineForItinerary.useQuery(
    { itineraryId },
    { enabled: isAuthenticated && !isOwner }
  );
  const myReview = mineQuery.data ?? null;

  const invalidateAll = async () => {
    await Promise.all([
      utils.itineraryReview.listForItinerary.invalidate({ itineraryId }),
      utils.itineraryReview.getMineForItinerary.invalidate({ itineraryId }),
      utils.itinerary.getBySlug.invalidate({ slug }),
    ]);
  };

  const createReview = trpc.itineraryReview.create.useMutation({
    onSuccess: async () => {
      setMode('idle');
      setFormError(null);
      setConflictNotice(null);
      await invalidateAll();
    },
    onError: async (error) => {
      if (errorCode(error) === 'CONFLICT') {
        // Already reviewed (e.g. submitted from another tab) — recover into
        // the edit path instead of stranding the draft on a raw error.
        setMode('idle');
        setFormError(null);
        setConflictNotice("You've already reviewed this trip. Edit your existing review below.");
        await utils.itineraryReview.getMineForItinerary.invalidate({
          itineraryId,
        });
        return;
      }
      setFormError(formatTrpcError(error));
    },
  });

  const updateReview = trpc.itineraryReview.update.useMutation({
    onSuccess: async () => {
      setMode('idle');
      setFormError(null);
      await invalidateAll();
    },
    onError: (error) => setFormError(formatTrpcError(error)),
  });

  const deleteReview = trpc.itineraryReview.softDelete.useMutation({
    onSuccess: async () => {
      setDeleteTargetId(null);
      setConflictNotice(null);
      await invalidateAll();
    },
    onError: (error) => {
      setDeleteTargetId(null);
      setFormError(formatTrpcError(error));
    },
  });

  const flagReview = trpc.itineraryReview.flag.useMutation({
    onSuccess: async () => {
      setFlagTargetId(null);
      await invalidateAll();
    },
    onError: (error) => {
      setFlagTargetId(null);
      setFormError(formatTrpcError(error));
    },
  });

  const mutating =
    createReview.isPending ||
    updateReview.isPending ||
    deleteReview.isPending ||
    flagReview.isPending;

  const handleCreate = (draft: ItineraryReviewDraft) => {
    setFormError(null);
    createReview.mutate({ itineraryId, ...draft });
  };

  const handleUpdate = (draft: ItineraryReviewDraft) => {
    if (!myReview) return;
    setFormError(null);
    updateReview.mutate({ reviewId: myReview._id, ...draft });
  };

  // The viewer may write a review: authed, not the owner, target still
  // public + published, and they have no live review of it already.
  const canWriteReview = isAuthenticated && !isOwner && isReviewableTarget && !myReview;

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  // Own review is pinned above the list — drop it from the list body so it is
  // not rendered twice (a flagged own review is not in the list at all).
  const otherItems = myReview ? items.filter((r) => r._id !== myReview._id) : items;
  const hasMore = items.length < total;

  return (
    <section className="mt-6 border-t border-[#DBD4C9] pt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <Heading2 className="text-gray-900">Reviews</Heading2>
        {reviewCount > 0 ? (
          <div className="flex items-center gap-2">
            <StarDisplay value={averageRating} size="md" />
            <Caption className="text-gray-600">
              {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
            </Caption>
          </div>
        ) : (
          <Caption className="text-gray-600">No reviews yet</Caption>
        )}
      </div>

      {conflictNotice && (
        <BodyText className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-gray-800">
          {conflictNotice}
        </BodyText>
      )}

      {formError && mode === 'idle' && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {formError}
        </p>
      )}

      {/* Write / edit affordance. The owner never sees one (the router would
          reject it), and neither does a viewer of a non-public target. */}
      <div className="mt-4">
        {mode === 'create' && (
          <AddItineraryReview
            heading="Write a review"
            submitLabel="Post review"
            onSubmit={handleCreate}
            onCancel={() => {
              setMode('idle');
              setFormError(null);
            }}
            isSubmitting={createReview.isPending}
            submitError={formError}
          />
        )}

        {mode === 'edit' && myReview && (
          <AddItineraryReview
            heading="Edit your review"
            initialRating={myReview.rating}
            initialBody={myReview.body}
            submitLabel="Save changes"
            onSubmit={handleUpdate}
            onCancel={() => {
              setMode('idle');
              setFormError(null);
            }}
            isSubmitting={updateReview.isPending}
            submitError={formError}
          />
        )}

        {mode === 'idle' && canWriteReview && (
          <Button variant="primary" onClick={() => setMode('create')}>
            Write a review
          </Button>
        )}

        {mode === 'idle' && !isAuthenticated && isReviewableTarget && (
          <Button variant="outline" onClick={() => navigate('/login')}>
            Sign in to review
          </Button>
        )}
      </div>

      {/* Review list. The viewer's own review is pinned first. */}
      <div className="mt-4">
        {listQuery.isLoading && <BodyText className="text-gray-500">Loading reviews…</BodyText>}

        {listQuery.isError && (
          <p className="text-sm text-red-600" role="alert">
            Reviews could not be loaded. {formatTrpcError(listQuery.error)}
          </p>
        )}

        {!listQuery.isLoading && !listQuery.isError && (
          <>
            {myReview && mode !== 'edit' && (
              <ItineraryReviewRow
                review={{ ...myReview, authorHandle: 'You' }}
                isOwn
                canFlag={false}
                onEdit={() => {
                  setFormError(null);
                  setMode('edit');
                }}
                onDelete={() => setDeleteTargetId(myReview._id)}
                onFlag={() => undefined}
                actionsDisabled={mutating}
              />
            )}

            {otherItems.map((review) => (
              <ItineraryReviewRow
                key={review._id}
                review={review}
                isOwn={Boolean(user && user.id === review.userId)}
                canFlag={Boolean(isAuthenticated && (!user || user.id !== review.userId))}
                onEdit={() => {
                  setFormError(null);
                  setMode('edit');
                }}
                onDelete={() => setDeleteTargetId(review._id)}
                onFlag={() => setFlagTargetId(review._id)}
                actionsDisabled={mutating}
              />
            ))}

            {otherItems.length === 0 && !myReview && (
              <BodyText className="text-gray-600">No one has reviewed this itinerary yet.</BodyText>
            )}

            {hasMore && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => setLimit((n) => n + PAGE_SIZE)}
                  loading={listQuery.isFetching}
                  disabled={listQuery.isFetching}
                >
                  Show more reviews
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (deleteTargetId) deleteReview.mutate({ reviewId: deleteTargetId });
        }}
        title="Delete your review?"
        message="Your review will be removed from this itinerary and from its rating. This cannot be undone."
        confirmText="Delete review"
        isDestructive
        isLoading={deleteReview.isPending}
      />

      <ConfirmationModal
        isOpen={flagTargetId !== null}
        onClose={() => setFlagTargetId(null)}
        onConfirm={() => {
          if (flagTargetId) flagReview.mutate({ reviewId: flagTargetId });
        }}
        title="Report this review?"
        message="A moderator will look at this review. It stays hidden from the itinerary's rating while it is under review."
        confirmText="Report review"
        isLoading={flagReview.isPending}
      />
    </section>
  );
};
